/**
PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 *  Copyright © 2025 Pearson Education, Inc.
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
* */

/**
 * commonStyles
 *
 * @file CommonStyles.jsx
 * @author Mohamed Yasar arabath M
 * @since 1.0.0
 */

import mixins from './mixins.scss';


const CommonStyles = () => ({
  subtitle2: {
    color: mixins.color_text_primary,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight600,
    lineHeight: mixins.lineHeight150
  },
  studyPrepTitle: {
    color: mixins.color_text_primary,
    cursor: 'initial',
    padding: '0px 8px',
    fontSize: mixins.fontSize20,
    fontStyle: 'normal',
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight500,
    lineHeight: mixins.lineHeight175,
    letterSpacing: 0,
    fontFeatureSettings: '\'liga\' off'
  },
  studyPrepCard: {
    gap: 8
  },
  studyPrepCardHeader: {
    minHeight: 'unset',
    alignItems: 'flex-start'
  },
  attemptsCount: {
    color: mixins.color_text_primary,
    fontSize: mixins.fontSize12,
    fontStyle: 'normal',
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight600,
    lineHeight: mixins.lineHeight16
  },
  mcqOptionSubLabel: {
    fontSize: mixins.fontSize12,
    fontStyle: 'normal',
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight600,
    lineHeight: mixins.lineHeight20
  },
  subscriptionStatusLabel: {
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight500,
    letterSpacing: '0.24px'
  },
  dynamicToggleLabel: {
    color: mixins.navyColor,
    fontSize: mixins.fontSize14,
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight600,
    lineHeight: mixins.lineHeight22
  },
  assignmentCardSectionsLabel: {
    letterSpacing: 'normal',
    fontFeatureSettings: '\'liga\' off'
  },
  secondaryButton: {
    display: 'flex',
    height: '40px',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '8px',
    padding: 8,
    minWidth: 'unset',
    position: 'relative',
    borderRadius: '1000px',
    border: `2px solid ${mixins.navyColor}`,

    '& span': {
      minWidth: 'unset'
    },

    '&:hover': {
      background: mixins.secondaryButtonHoverColor
    }
  },
  secondaryButtonText: {
    color: mixins.navyColor,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '22px',
    padding: '0px 8px 2px 8px'
  },
  buttonContentSpan: {
    margin: 0,
    display: 'flex',
    padding: '0px 8px 2px 8px',
    minWidth: 'unset',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8
  },
  focusVisible: {
    '&&': {
      '&::after': {
        borderColor: mixins.secondaryButtonFocusColor,
        borderRadius: 'inherit'
      }
    }
  },
  iconButton: {
    display: 'flex',
    width: '40px',
    height: '40px',
    padding: '12px',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
    color: mixins.navyColor,

    '&:hover': {
      background: mixins.secondaryButtonHoverColor
    }
  },
  /**
   * scrollContainer - Custom scrollbar styling for containers with scrollable content
   *
   * This style object provides cross-browser scrollbar customization:
   * - Firefox: Uses scrollbarWidth and scrollbarColor properties
   * - Webkit browsers (Chrome, Safari, Edge): Uses ::-webkit-scrollbar pseudo-elements
   */
  scrollContainer: ({
    scrollbarWidth = 8,
    scrollbarColor = 'rgba(81, 46, 171, 0.15) transparent',
    borderRadius = 'inherit',
    border = 'none',
    overflowY = 'auto'
  }) => ({
    overflowY,
    boxSizing: 'border-box',

    // paddingRight: '12px',
    // marginRight: '4px',

    // Firefox: Set scrollbar width to 'thin' (narrower than default)
    scrollbarWidth: 'thin',
    borderRadius,
    border,

    // Firefox: Set scrollbar colors (thumb color, track color)
    // First color (#512EAB26) is the thumb, second (transparent) is the track
    scrollbarColor,

    /**
     * Webkit browsers (Chrome, Safari, Edge) scrollbar customization
     * ::-webkit-scrollbar targets the entire scrollbar element
     */
    '&::-webkit-scrollbar': {
      width: scrollbarWidth, // Set scrollbar width to 8px
      background: 'transparent' // Make scrollbar background transparent
    },

    /**
     * ::-webkit-scrollbar-track targets the scrollbar track (the area behind the thumb)
     * The track is the "groove" or "channel" that the scrollbar thumb moves along
     */
    '&::-webkit-scrollbar-track': {
      background: 'transparent' // Make track background transparent
    },

    /**
     * ::-webkit-scrollbar-thumb targets the scrollbar thumb (the draggable part)
     * The thumb is the actual scrollbar handle that users drag to scroll
     */
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: scrollbarColor, // Semi-transparent purple color for the thumb
      borderRadius: 8, // Rounded corners for the thumb
      border: 'none' // Remove any border around the thumb
    },

    /**
     * ::-webkit-scrollbar-thumb:hover targets the thumb when hovered
     * Provides visual feedback when user hovers over the scrollbar
     */
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: scrollbarColor // Slightly more opaque purple on hover
    },

    /**
     * ::-webkit-scrollbar-button targets the scrollbar buttons (up/down arrows)
     * These are the small buttons at the top and bottom of the scrollbar
     */
    '&::-webkit-scrollbar-button': {
      display: 'none' // Hide the scrollbar buttons completely
    },

    /**
     * ::-webkit-scrollbar-corner targets the corner where scrollbars meet
     * This is the small square area where horizontal and vertical scrollbars intersect
     */
    '&::-webkit-scrollbar-corner': {
      background: 'transparent' // Make the corner area transparent
    }
  }),
  /**
   * truncateNumberOfLines - Truncate text to a specified number of lines
   *
   * This style object truncates text to a specified number of lines.
   *
   * @param {number} lines - The number of lines to truncate the text to
   * @returns {object} The style object
   */
  truncateNumberOfLines: (lines) => ({
    display: '-webkit-box',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: lines,
    lineClamp: lines
  }),
  buttonReset: {
    width: '100%',
    background: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    color: 'inherit',
    font: 'inherit',
    textAlign: 'inherit',
    cursor: 'pointer',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    appearance: 'none',
    '&:focus-visible': {
      outline: '2px solid #1976d2',
      outlineOffset: 2
    }
  }
});

export default CommonStyles;
